import React from "react"
import { Navigate, useLocation } from "react-router-dom"

import { FEATURES_TYPE } from "@constants"
import { DS_REPRICING_ROUTES } from "@constants/routes"
import AppLayout from "Layout"
import DsRepricingStrategiesPage from "Pages/DsRepricing/Page"
import DsRepricingSetupPage      from "Pages/DsRepricing/Setup"
import DsRepricingActivityPage   from "Pages/DsRepricing/Activity"
import DsRepricingSchedulePage   from "Pages/DsRepricing/Schedule"
import DsRepricingExecutionsPage from "Pages/DsRepricing/Executions"
import DsRepricingLastChangesPage from "Pages/DsRepricing/LastChanges"
import { newBadge } from "Layout/menu"
import NoAccess from "@components/NoAccess"
import { noAccessChildren } from "@components/NoAccess/@services/access"

/** Bounce to /strategies keeping `?rule=<id>` so the deep link still opens
 *  the strategy the overlap modal pointed at. */
function LegacyConfigurationRedirect() {
	const { search } = useLocation()
	return <Navigate to={{ pathname: DS_REPRICING_ROUTES.STRATEGIES_PATH, search }} replace/>
}

/** The Scheduler is gone from the menu (CU-869fhuhy6, UX D10): `/schedule`
 *  and `/schedule?tab=*` bounce to Executions, query kept. */
function LegacyScheduleRedirect() {
	const { search } = useLocation()
	const params = new URLSearchParams(search)
	params.delete('tab')
	const qs = params.toString()
	return <Navigate to={{ pathname: DS_REPRICING_ROUTES.EXECUTIONS_PATH, search: qs ? `?${qs}` : '' }} replace/>
}

/** Without the flag the address is still served, off the menu (Layout filters sections by `featuresV2[id]`), and
 *  shows "no access" instead of the 404 (CU-869fcezdm; patterns.md K4). */
export const DsRepricingRoutes = userData => {
	const section = children => [{
		id: FEATURES_TYPE.DS_REPRICING,
		path: DS_REPRICING_ROUTES.PATH,
		title: DS_REPRICING_ROUTES.TITLE,
		icon: 'arrow-trend-up',
		element: <AppLayout/>,
		children,
	}]

	if (!userData?.featuresV2?.ds_repricing) return section(noAccessChildren(<NoAccess name={DS_REPRICING_ROUTES.TITLE}/>))

	return section([
			{
				index: true,
				element: <Navigate to={DS_REPRICING_ROUTES.STRATEGIES_PATH} replace/>,
			},
			{
				path: DS_REPRICING_ROUTES.STRATEGIES_PATH,
				title: DS_REPRICING_ROUTES.STRATEGIES_TITLE,
				element: <DsRepricingStrategiesPage/>,
			},
			{
				path: DS_REPRICING_ROUTES.SETUP_PATH,
				title: DS_REPRICING_ROUTES.SETUP_TITLE,
				element: <DsRepricingSetupPage/>,
			},
			{
				path: DS_REPRICING_ROUTES.ACTIVITY_PATH,
				title: DS_REPRICING_ROUTES.ACTIVITY_TITLE,
				element: <DsRepricingActivityPage/>,
			},
			// Last changes (CU-869fn0gcp), right after Activity log (its aggregated reading; Curro D6). Gated by the
			// module like the rest, and by the deploy switch the session payload reports (`lastChangesOn`): off, no
			// entry and no route for anyone — a hand-typed address meets the app's 404 (criterion 2).
			...(userData?.lastChangesOn ? [{
				path: DS_REPRICING_ROUTES.LAST_CHANGES_PATH,
				title: DS_REPRICING_ROUTES.LAST_CHANGES_TITLE,
				element: <DsRepricingLastChangesPage/>,
				...newBadge('lastChanges', new Date()),
			}] : []),
			{
				path: DS_REPRICING_ROUTES.EXECUTIONS_PATH,
				title: DS_REPRICING_ROUTES.EXECUTIONS_TITLE,
				element: <DsRepricingExecutionsPage/>,
			},
			// Legacy alias since the 28 re-cut: the Scheduler window is gone
			// from the menu; its bookmarks land on Executions.
			{
				path: DS_REPRICING_ROUTES.SCHEDULE_PATH,
				element: <LegacyScheduleRedirect/>,
				noMenu: true,
			},
			// The old Schedule window, read-only, reached from the migration
			// banner until the migration is validated. Hidden from the menu.
			{
				path: DS_REPRICING_ROUTES.SCHEDULE_LEGACY_PATH,
				element: <DsRepricingSchedulePage/>,
				noMenu: true,
			},
			// Legacy alias: bookmarks pointing at /blocked land here
			// and get bounced to /activity with the blocked filter
			// pre-applied. Hidden from the menu.
			{
				path: DS_REPRICING_ROUTES.BLOCKED_PATH,
				element: <Navigate to={`${DS_REPRICING_ROUTES.ACTIVITY_PATH}?event_type=blocked_by_limit&only_pending=1`} replace/>,
				noMenu: true,
			},
			// Legacy alias: bookmarks pointing at /configuration land here
			// and get bounced to /strategies. Hidden from the menu.
			//
			// The query string has to survive the bounce: the overlap modal
			// opens the conflicting strategy as `…/configuration?rule=<id>`,
			// and a `Navigate` with a plain string destination drops the
			// search, landing the operator on the list with no drawer — right
			// after a 409, which is exactly when they need to reach the other
			// strategy (CU-869f043c1 QA, defect 2).
			{
				path: DS_REPRICING_ROUTES.CONFIGURATION_PATH,
				element: <LegacyConfigurationRedirect/>,
				noMenu: true,
			},
	])
}
